/**
 * Schedule time helpers — custom HH:mm ranges, lunch row, slot labels.
 */

import { TIME_SLOTS, type TimeSlot } from './types';

export const LUNCH_SLOT_ID = 'lunch';

/** Rows the schedule builder may assign (standard slots + lunch band). */
export const SCHEDULE_ASSIGNABLE_SLOT_IDS: string[] = [
  ...TIME_SLOTS.map((s) => s.id),
  LUNCH_SLOT_ID,
];

const HM_REGEX = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export function isValidScheduleHm(value: string | undefined | null): boolean {
  if (value == null || typeof value !== 'string') return false;
  return HM_REGEX.test(value.trim());
}

/** Minutes from midnight for sorting / overlap. */
export function hmToMinutes(hm: string): number {
  const [h, m] = hm.trim().split(':').map(Number);
  return h * 60 + m;
}

export function formatHmTo12h(hm: string): string {
  const [hRaw, mRaw] = hm.trim().split(':').map(Number);
  const h = hRaw ?? 0;
  const m = mRaw ?? 0;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function hmMeridiem(hm: string): 'am' | 'pm' {
  const h = Number(hm.trim().split(':')[0]) || 0;
  return h >= 12 ? 'pm' : 'am';
}

/** Hour:minute without meridiem, dropping `:00`. e.g. `10`, `9:30`, `12`. */
function formatHmDigits(hm: string): string {
  const [hRaw, mRaw] = hm.trim().split(':').map(Number);
  const h = hRaw ?? 0;
  const m = mRaw ?? 0;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}` : `${h12}:${String(m).padStart(2, '0')}`;
}

/** Compact single time: drop `:00`, lowercase meridiem, no space. e.g. `10am`, `9:30am`, `12pm`. */
export function formatHmCompact(hm: string): string {
  if (!isValidScheduleHm(hm)) return hm;
  return `${formatHmDigits(hm)}${hmMeridiem(hm)}`;
}

export function formatScheduleTimeRange(
  start?: string | null,
  end?: string | null
): string | null {
  const s = start?.trim();
  const e = end?.trim();
  if (s && e && isValidScheduleHm(s) && isValidScheduleHm(e)) {
    return `${formatHmTo12h(s)} – ${formatHmTo12h(e)}`;
  }
  if (s && isValidScheduleHm(s)) return formatHmTo12h(s);
  if (e && isValidScheduleHm(e)) return formatHmTo12h(e);
  return null;
}

export function getTimeSlotById(slotId: string | undefined): TimeSlot | undefined {
  if (!slotId || slotId === LUNCH_SLOT_ID) return undefined;
  return TIME_SLOTS.find((s) => s.id === slotId);
}

/** Display label for a class on schedule / enroll / PDF. */
export function getClassScheduleTimeLabel(cls: {
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  timeSlotId?: string | null;
  preferredTimeSlotId?: string | null;
  preferredScheduleStartTime?: string | null;
  preferredScheduleEndTime?: string | null;
}): string | null {
  const custom = formatScheduleTimeRange(cls.scheduleStartTime, cls.scheduleEndTime);
  if (custom) return custom;
  const slot = getTimeSlotById(cls.timeSlotId ?? cls.preferredTimeSlotId ?? undefined);
  if (slot) return slot.label;
  const pref = formatScheduleTimeRange(
    cls.preferredScheduleStartTime,
    cls.preferredScheduleEndTime
  );
  return pref;
}

export function defaultTimesForSlot(slotId: string): { start: string; end: string } | null {
  const slot = getTimeSlotById(slotId);
  if (!slot) {
    if (slotId === LUNCH_SLOT_ID) return { start: '12:00', end: '12:30' };
    return null;
  }
  return { start: slot.startTime, end: slot.endTime };
}

/** Pick assignable row id from preferred/scheduled custom times (max overlap with a slot). */
export function inferSlotIdFromTimes(start?: string | null, end?: string | null): string | null {
  const s = start?.trim();
  const e = end?.trim();
  if (!s || !e || !isValidScheduleHm(s) || !isValidScheduleHm(e)) return null;
  const startMin = hmToMinutes(s);
  const endMin = hmToMinutes(e);
  if (endMin <= startMin) return null;

  let bestId: string | null = null;
  let bestOverlap = -1;
  for (const slot of TIME_SLOTS) {
    const slotStart = hmToMinutes(slot.startTime);
    const slotEnd = hmToMinutes(slot.endTime);
    const overlap = Math.min(endMin, slotEnd) - Math.max(startMin, slotStart);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      bestId = slot.id;
    }
  }
  if (bestOverlap > 0 && bestId) return bestId;
  if (startMin >= hmToMinutes('12:00') && endMin <= hmToMinutes('13:30')) return LUNCH_SLOT_ID;
  return bestId;
}

export function classHasAssignedScheduleSlot(cls: {
  timeSlotId?: string | null;
}): boolean {
  const id = typeof cls.timeSlotId === 'string' ? cls.timeSlotId.trim() : '';
  return id.length > 0 && SCHEDULE_ASSIGNABLE_SLOT_IDS.includes(id);
}

/** Legacy-style room label: `(106)` or `(Upstairs Library)`. */
export function formatRoomForSchedule(roomNumber?: string | null): string | null {
  const raw = roomNumber?.trim();
  if (!raw) return null;
  if (raw.startsWith('(') && raw.endsWith(')')) return raw;
  return `(${raw})`;
}

/**
 * Compact range like printed schedules, optimized for space:
 * drop `:00`, lowercase am/pm, and show the meridiem once when both ends share it.
 * e.g. `10–11am`, `12:30–1:30pm`, `9:30am–12pm`.
 */
export function formatScheduleTimeRangeCompact(
  start?: string | null,
  end?: string | null
): string | null {
  const s = start?.trim();
  const e = end?.trim();
  if (s && e && isValidScheduleHm(s) && isValidScheduleHm(e)) {
    const sMeridiem = hmMeridiem(s);
    const eMeridiem = hmMeridiem(e);
    if (sMeridiem === eMeridiem) {
      return `${formatHmDigits(s)}–${formatHmDigits(e)}${eMeridiem}`;
    }
    return `${formatHmCompact(s)}–${formatHmCompact(e)}`;
  }
  if (s && isValidScheduleHm(s)) return formatHmCompact(s);
  if (e && isValidScheduleHm(e)) return formatHmCompact(e);
  return null;
}

/** Stacked start/end lines for the grid time column (compact: `10am` / `11am`). */
export function getTimeSlotColumnLines(slotId: string): string[] {
  if (slotId === LUNCH_SLOT_ID) {
    return [formatHmCompact('12:00'), formatHmCompact('12:30')];
  }
  const slot = getTimeSlotById(slotId);
  if (slot) {
    return [formatHmCompact(slot.startTime), formatHmCompact(slot.endTime)];
  }
  return [];
}

export interface ScheduleClassDisplay {
  title: string;
  /** Meeting time under the title when set or when it differs from the row band. */
  timeLabel: string | null;
  /** Parenthetical room, e.g. `(219)`. */
  roomLabel: string | null;
  /** True when meeting times extend outside the assigned row's default band. */
  extendsBeyondSlot: boolean;
}

function getClassMeetingTimes(cls: {
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  timeSlotId?: string | null;
}): { start: string; end: string } | null {
  const s = cls.scheduleStartTime?.trim();
  const e = cls.scheduleEndTime?.trim();
  if (s && e && isValidScheduleHm(s) && isValidScheduleHm(e)) {
    return { start: s, end: e };
  }
  const slot = getTimeSlotById(cls.timeSlotId ?? undefined);
  if (slot) return { start: slot.startTime, end: slot.endTime };
  return null;
}

/** Whether custom meeting times spill outside the assigned grid row band. */
export function classMeetingExtendsBeyondSlot(cls: {
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  timeSlotId?: string | null;
}): boolean {
  const meeting = getClassMeetingTimes(cls);
  if (!meeting || !cls.timeSlotId) return false;
  const band = defaultTimesForSlot(cls.timeSlotId);
  if (!band) return false;
  return (
    hmToMinutes(meeting.start) < hmToMinutes(band.start) ||
    hmToMinutes(meeting.end) > hmToMinutes(band.end)
  );
}

/**
 * How a class appears on the schedule grid and PDF (title, optional time, optional room).
 * Row = placement band; time under title = actual Thursday meeting time when customized.
 */
export function getScheduleClassDisplay(cls: {
  title?: string | null;
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  timeSlotId?: string | null;
  roomNumber?: string | null;
}): ScheduleClassDisplay {
  const title = cls.title?.trim() || '—';
  const roomLabel = formatRoomForSchedule(cls.roomNumber);
  const hasCustom =
    isValidScheduleHm(cls.scheduleStartTime) && isValidScheduleHm(cls.scheduleEndTime);
  const extendsBeyondSlot = classMeetingExtendsBeyondSlot(cls);

  let timeLabel: string | null = null;
  if (hasCustom) {
    timeLabel = formatScheduleTimeRangeCompact(cls.scheduleStartTime, cls.scheduleEndTime);
  } else if (extendsBeyondSlot) {
    const meeting = getClassMeetingTimes(cls);
    if (meeting) {
      timeLabel = formatScheduleTimeRangeCompact(meeting.start, meeting.end);
    }
  }

  return { title, timeLabel, roomLabel, extendsBeyondSlot };
}
